// src/pages/SettingsUsers.tsx
import { useEffect, useState } from "react";
import { supabase } from "../lib/supabase";
import { useCampaignId } from "../hooks/useCampaignId";

type CampaignUser = {
  id: string;
  role: string;
  invitation_status: string;
  user: { id: string; email: string | null };
};
type InviteRow = {
  id: string;
  email: string;
  role: string;
  status: string;
  created_at: string;
};

export default function SettingsUsers() {
  const campaignId = useCampaignId();

  const [rows, setRows] = useState<CampaignUser[]>([]);
  const [invites, setInvites] = useState<InviteRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);

  // Invite form
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState("volunteer");
  const [inviting, setInviting] = useState(false);
  const [inviteMsg, setInviteMsg] = useState<string | null>(null);

  // Load members + invites when campaignId is known
  useEffect(() => {
    if (!campaignId) return;
    (async () => {
      try {
        setErr(null);
        setLoading(true);

        const { data: users, error: usersErr } = await supabase
          .from("campaign_users")
          .select(`
            id,
            role,
            invitation_status,
            user:users!campaign_users_user_id_fkey ( id, email )
          `)
          .eq("campaign_id", campaignId)
          .order("role", { ascending: true })
          .limit(1000);
        if (usersErr) throw usersErr;

        const { data: invs, error: invErr } = await supabase
          .from("campaign_invites")
          .select("id,email,role,status,created_at")
          .eq("campaign_id", campaignId)
          .order("created_at", { ascending: false })
          .limit(1000);
        if (invErr) throw invErr;

        setRows((users as any) || []);
        setInvites((invs as any) || []);
      } catch (e: any) {
        setErr(e.message || String(e));
      } finally {
        setLoading(false);
      }
    })();
  }, [campaignId]);

  async function sendInvite(e: React.FormEvent) {
    e.preventDefault();
    setInviteMsg(null);
    setErr(null);
    try {
      if (!campaignId) throw new Error("No campaign selected.");
      if (!inviteEmail) throw new Error("Email is required.");

      setInviting(true);

      // record invite
      const { error: insertErr } = await supabase.from("campaign_invites").insert({
        campaign_id: campaignId,
        email: inviteEmail.trim().toLowerCase(),
        role: inviteRole,
        status: "pending",
      });
      if (insertErr) throw insertErr;

      // magic link -> callback -> go to accept-invite
      const acceptUrl = new URL(`${window.location.origin}/accept-invite`);
      acceptUrl.searchParams.set("campaign_id", campaignId);
      acceptUrl.searchParams.set("role", inviteRole);

      const { error: otpErr } = await supabase.auth.signInWithOtp({
        email: inviteEmail.trim().toLowerCase(),
        options: {
          emailRedirectTo: `${window.location.origin}/auth/callback?next=${encodeURIComponent(
            acceptUrl.toString()
          )}`,
        },
      });
      if (otpErr) throw otpErr;

      setInviteMsg("Invite sent. Ask them to check their email.");
      setInviteEmail("");
      setInviteRole("volunteer");

      // refresh invites
      const { data: invs } = await supabase
        .from("campaign_invites")
        .select("id,email,role,status,created_at")
        .eq("campaign_id", campaignId)
        .order("created_at", { ascending: false })
        .limit(1000);
      setInvites((invs as any) || []);
    } catch (e: any) {
      setErr(e.message || String(e));
    } finally {
      setInviting(false);
    }
  }

  async function revokeInvite(inviteId: string) {
    try {
      setErr(null);
      const { error } = await supabase
        .from("campaign_invites")
        .update({ status: "revoked" })
        .eq("id", inviteId)
        .eq("status", "pending");
      if (error) throw error;
      setInvites((prev) =>
        prev.map((i) => (i.id === inviteId ? { ...i, status: "revoked" } : i))
      );
    } catch (e: any) {
      setErr(e.message || String(e));
    }
  }

  return (
    <div style={{ padding: 16 }}>
      <h1 style={{ fontSize: 22, fontWeight: 700, marginBottom: 8 }}>Users</h1>
      <p style={{ color: "#6b7280", marginBottom: 16 }}>
        Manage campaign users and invitations.
      </p>

      {err && (
        <div style={{ background: "#ffe8e8", border: "1px solid #f2b0b0", padding: 10, borderRadius: 8, color: "#a40000", marginBottom: 10 }}>
          {err}
        </div>
      )}

      {!campaignId ? (
        <div>Select a campaign from the top menu to manage users.</div>
      ) : loading ? (
        <div>Loading…</div>
      ) : (
        <>
          {/* Invite Form */}
          <div style={{ border: "1px solid #e5e7eb", borderRadius: 12, padding: 14, marginBottom: 16 }}>
            <h2 style={{ fontSize: 16, fontWeight: 700, marginBottom: 8 }}>Invite a user</h2>
            <form onSubmit={sendInvite} style={{ display: "grid", gridTemplateColumns: "2fr 1fr auto", gap: 8 }}>
              <input
                type="email"
                placeholder="person@example.com"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                required
                style={input}
              />
              <select value={inviteRole} onChange={(e) => setInviteRole(e.target.value)} style={input}>
                <option value="volunteer">Volunteer</option>
                <option value="staff">Staff</option>
                <option value="admin">Admin</option>
                <option value="owner">Owner</option>
              </select>
              <button type="submit" disabled={inviting} style={btnPrimary}>
                {inviting ? "Sending…" : "Send Invite"}
              </button>
            </form>
            {inviteMsg && <div style={{ marginTop: 8, color: "#166534" }}>{inviteMsg}</div>}
          </div>

          {/* Current Members */}
          <h3 style={{ fontSize: 16, fontWeight: 700, marginTop: 6, marginBottom: 8 }}>Current Members</h3>
          {rows.length === 0 ? (
            <div>No users yet.</div>
          ) : (
            <table style={{ width: "100%", borderCollapse: "collapse", marginBottom: 16 }}>
              <thead>
                <tr>
                  <th style={th}>Email</th>
                  <th style={th}>Role</th>
                  <th style={th}>Invitation</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id}>
                    <td style={td}>{r.user?.email || "—"}</td>
                    <td style={td}>{r.role}</td>
                    <td style={td}>{r.invitation_status}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          {/* Pending Invites */}
          <h3 style={{ fontSize: 16, fontWeight: 700, marginTop: 6, marginBottom: 8 }}>Invitations</h3>
          {invites.length === 0 ? (
            <div>No invitations yet.</div>
          ) : (
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr>
                  <th style={th}>Email</th>
                  <th style={th}>Role</th>
                  <th style={th}>Status</th>
                  <th style={th}>Sent</th>
                  <th style={th}></th>
                </tr>
              </thead>
              <tbody>
                {invites.map((i) => (
                  <tr key={i.id}>
                    <td style={td}>{i.email}</td>
                    <td style={td}>{i.role}</td>
                    <td style={td}>{i.status}</td>
                    <td style={td}>{new Date(i.created_at).toLocaleString()}</td>
                    <td style={td}>
                      {i.status === "pending" && (
                        <button onClick={() => revokeInvite(i.id)} style={btnSmall}>
                          Revoke
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </>
      )}
    </div>
  );
}

const input: React.CSSProperties = {
  padding: 10,
  border: "1px solid #ddd",
  borderRadius: 8,
  fontSize: 14,
};

const btnPrimary: React.CSSProperties = {
  padding: "10px 12px",
  border: "1px solid #0ea5e9",
  borderRadius: 8,
  background: "#0ea5e9",
  color: "#fff",
  cursor: "pointer",
};

const btnSmall: React.CSSProperties = {
  padding: "6px 10px",
  border: "1px solid #ddd",
  borderRadius: 8,
  background: "#fff",
  cursor: "pointer",
  fontSize: 13,
};

const th: React.CSSProperties = {
  textAlign: "left",
  padding: "8px 10px",
  borderBottom: "1px solid #eee",
  fontWeight: 600,
  fontSize: 14,
  color: "#374151",
};

const td: React.CSSProperties = {
  padding: "8px 10px",
  borderBottom: "1px solid #f3f4f6",
  fontSize: 14,
  color: "#111827",
};
